import React, { useState, useEffect, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  signInWithEmailLink,
} from "firebase/auth";
import { auth } from "./firebaseConfig";
import { UserAttributes } from "../slices/types";

// --- Interfaces ---
interface AuthContextProps {
  user: User | null;
  userAttributes: UserAttributes | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInUsingEmailLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  isError: boolean;
  setUserAttributes: (userAttributes: UserAttributes) => void;
}

// --- Context ---
const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined
);

// --- Provider ---
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [userAttributes, setUserAttributes] = useState<UserAttributes>(
    {} as UserAttributes
  );

  // --- Authentication Functions ---

  const signIn = useCallback(async (email: string, password: string) => {
    setIsError(false);
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setUser(userCredential.user);
    } catch (err) {
      setIsError(true);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInUsingEmailLink = useCallback(async (email: string) => {
    setIsError(false);
    setLoading(true);
    try {
      const userCredential = await signInWithEmailLink(
        auth,
        email,
        window.location.href
      );
      setUser(userCredential.user);
    } catch (err) {
      setIsError(true);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOutUser = useCallback(async () => {
    setIsError(false);
    setLoading(true);
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      setIsError(true);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // --- Effect for Initial Auth State ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- Context Value ---
  const value: AuthContextProps = {
    user,
    userAttributes,
    signIn,
    signOut: signOutUser,
    loading,
    isError,
    signInUsingEmailLink,
    setUserAttributes: (userAttributes: UserAttributes) => {
      setUserAttributes(userAttributes);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// --- Hook ---
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
